import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell'
import { useObserver } from 'mobx-react-lite'
import React, { FC, memo, useMemo } from 'react'
import FieldSkeleton from '@api/order/components/CellLoaders/FieldSkeleton'
import { makeStyles } from '@material-ui/core'
import formatNumberByLocale from '@utils/format-number-by-locale'
import IAProductQuantityActions from './IAProductQuantityActions'
import IAProductQuantityInputField from './IAProductQuantityInputField'
import IAReadOnlyField from './IAReadOnlyField'
import useOrderContext from '@modules/order/hooks/use-order-context'
import { useEnhancedProductStatus } from '@components/UI-Components/ProductCard/ProductStatusHook'
import { useTranslation } from 'react-i18next'
import useViewContext from '@modules/order/hooks/use-view-context'
import { InitialAllocationUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/InitialAllocationUIStore'
import { InitialAllocationDataStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/InitialAllocationDataStore'

const useStyles = makeStyles(theme => ({
  rootContainer: {
    width: '100%'
  }
}))

const IAProductQuantityCell: FC<CellItem> = ({ rowData, column }) =>
  useObserver(function useHook() {
    const classes = useStyles()
    const { t } = useTranslation()

    const {
      uiStore: {
        isOrderEditable,
        dataStore: { summary }
      }
    } = useOrderContext()
    const { uiStore } = useViewContext()
    const {
      getBookedDoors,
      rollUpIAProductQuantity,
      dataStore: { getIAProductData, getIAProductDataLoading }
    } = uiStore as InitialAllocationUIStore<InitialAllocationDataStore>

    const productRowId = rowData?.id
    const isProductActive: boolean = useEnhancedProductStatus(rowData)
    const bookedDoors = getBookedDoors(productRowId, summary?.doorOrders)
    const isAPICallInProgress = productRowId
      ? getIAProductDataLoading(productRowId)
      : true
    const disableRollUp = useMemo(() => {
      const temp = !isOrderEditable || !bookedDoors?.length || isAPICallInProgress
      return temp
    }, [isOrderEditable, bookedDoors, isAPICallInProgress])

    if (!productRowId) {
      return <FieldSkeleton />
    }

    const IAProductData = getIAProductData(productRowId) || {}
    const { clientOrderProductQuantity = 0, forecastQuantity } = rowData || {}
    const productQuantity = clientOrderProductQuantity || 0
    const centralProposal = forecastQuantity || 0
    const disabledProductQuantity =
      !isOrderEditable || !isProductActive || isAPICallInProgress

    return (
      <div className={classes.rootContainer} data-test-id='IAProductQuantityCell'>
        <IAReadOnlyField
          label={t('order.initialAllocation.centralPraposal', 'Central Proposal')}
          value={formatNumberByLocale(centralProposal)}
        />
        <IAReadOnlyField
          label={t('order.initialAllocation.finalOrder', 'Final Order')}
          value={formatNumberByLocale(productQuantity)}
        />
        <IAProductQuantityInputField
          rowData={rowData}
          IAProductData={IAProductData}
          disabled={disabledProductQuantity}
        />
        <IAProductQuantityActions
          rollUpIAProductQuantity={() => {
            rollUpIAProductQuantity(productRowId)
          }}
          disableRollUp={disableRollUp}
        />
      </div>
    )
  })

export default memo(IAProductQuantityCell)
